import { Form, Formik } from 'formik';
import {
  Button,
  Input,
  LoadingSpinner,
  Modal,
} from '@rabbit/elements/shared-components';
import * as Yup from 'yup';
import { useContext, useState } from 'react';
import { CaseflowContext } from 'apps/sage/src/context/CaseflowContext';
import { MANDRILL_TEMPLATES } from '@rabbit/bizproc/react';
import { getConsumerURL } from 'apps/sage/src/utils/helpers';
import { updateHoldingOnClose } from 'apps/sage/src/utils/firestore-helpers';
import { toast } from 'react-toastify';
import {
  FBD_Consumer_Private,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import { useTranslation } from 'react-i18next';
import { CORPORATE } from '@rabbit/sage/utils/consts.ts';
import { AppContext } from '@rabbit/app-context';

export interface LogRepairCompleteModalProps {}

interface FormValuesShape {
  cost_to_repair: { amount: number; currency: string };
  internal_comment: string;
  comment_to_customer: string;
}

const validationSchema = Yup.object().shape({
  cost_to_repair: Yup.object({
    amount: Yup.number().required('Please enter a valid amount'),
    currency: Yup.string().trim().required('Currency is required'),
  }).required('Please enter a repair cost'),
  internal_comment: Yup.string().trim(),
  comment_to_customer: Yup.string().trim(),
});

export function LogRepairCompleteModal({}: LogRepairCompleteModalProps) {
  const { t } = useTranslation();
  const { tenantInfo } = useContext(AppContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInternalComment, setShowInternalComment] = useState(false);

  const initialValues = {
    cost_to_repair: {
      amount: 0,
      currency: tenantInfo?.currency ?? '',
    },
    internal_comment: '',
    comment_to_customer: '',
  };

  const {
    holdingProxyData,
    caseFacts,
    alterCaseFacts,
    alterCasePublicEmail,
    executeAction,
    moveSpotlight,
    setShowModal,
    operatingPersona,
    caseId,
  } = useContext(CaseflowContext) || {};

  if (
    !setShowModal ||
    !executeAction ||
    !alterCaseFacts ||
    !alterCasePublicEmail ||
    !moveSpotlight ||
    !operatingPersona ||
    !caseFacts ||
    !caseId
  )
    return <LoadingSpinner size={'xs'} />;
  const consumer_persona_id = caseFacts?.consumer_persona_id;
  const onSubmit = async (values: FormValuesShape) => {
    const { cost_to_repair, internal_comment, comment_to_customer } = values;

    setIsSubmitting(true);
    const consumerData = await FBD_Consumer_Private.get(
      consumer_persona_id ?? ''
    );
    await moveSpotlight(PersonaTypeSingleLetter.Consumer);
    await executeAction('log_authorised_repairer_repair_complete');

    const factsToAlter = {
      ...(internal_comment && {
        internal_comment: {
          comment: internal_comment,
          author: operatingPersona,
        },
      }),
      cost_to_repair: cost_to_repair,
      comment_to_customer: comment_to_customer,
    };

    const to = caseFacts?.consumer_email ?? '',
      from = tenantInfo?.emailInfo?.emailSender ?? '',
      template = MANDRILL_TEMPLATES.BODY_AUTHORISED_REPAIR_COMPLETE_V2,
      business_name = tenantInfo?.name ?? '',
      claim_id = caseId,
      first_name = caseFacts?.consumer_name ?? '',
      product_name = caseFacts.consumer_holding_name ?? '',
      subject = t('email.subject.repairComplete', {
        business_name,
        product_name,
      }),
      link_to_claim =
        consumerData?.consumer_type === CORPORATE
          ? `${window.location.origin}/claims/${caseId}`
          : `${getConsumerURL()}/repairs/${caseId}`;

    if (Object.keys(factsToAlter).length > 0) {
      await alterCaseFacts(factsToAlter);
      await alterCasePublicEmail({
        context: 'authorised_repair_complete',
        from,
        to,
        subject,
        template,
        substitutions: {
          subject,
          business_name,
          claim_id,
          first_name,
          comment_to_customer,
          product_name,
          link_to_claim,
        },
        holdingProxyLink: holdingProxyData?.docid ?? '',
        shallBeSentViaBackend: true,
      });
    }

    await updateHoldingOnClose(caseId, caseFacts.consumer_holding ?? '');
    setShowModal(false);
    toast.success(t('Claim updated successfully.'));
  };

  return (
    <Modal
      settings={{
        title: t('Log repair as complete'),
        headerBackground: true,
        handleClose: () => setShowModal(false),
      }}
      kind="generic"
      isLoading={isSubmitting}
      className="max-w-[1024px]"
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ errors, values }) => (
          <Form className="mt-5 flex flex-col gap-3 px-4">
            <Input
              type="currency"
              label="Cost of repair"
              name="cost_to_repair"
              settings={{
                id: 'cost_to_repair',
                placeholder: '',
                currency:
                  values?.cost_to_repair?.currency ?? tenantInfo?.currency,
                hint: '*required',
              }}
            />
            <div className="mt-4">
              <Input
                type="rich-text"
                name="comment_to_customer"
                label={t('Comment to customer')}
                settings={{
                  id: 'comment_to_customer',
                  placeholder: t('Comment to customer'),
                }}
              />
            </div>
            {!showInternalComment && (
              <div className="mt-4">
                <Button
                  kind="outline"
                  type="button"
                  className="w-full"
                  onClick={() => setShowInternalComment(true)}
                >
                  {t('Add internal comment')}
                </Button>
              </div>
            )}
            {showInternalComment && (
              <div>
                <Input
                  type="rich-text"
                  label={t('Internal comment')}
                  name="internal_comment"
                  settings={{
                    id: 'internal_comment',
                    placeholder: '',
                    allowSpecialCharacter: true,
                  }}
                />
              </div>
            )}
            <div className="mt-8 flex gap-8">
              <Button kind="primary" type="submit" loading={isSubmitting}>
                {t('Log as complete and close')}
              </Button>
              <Button kind="outline_red" onClick={() => setShowModal(false)}>
                {t('Cancel')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

export default LogRepairCompleteModal;
